import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { aiGovApi, clientsApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const P  = '#6B3FA0';
const PD = '#4A2B70';
const PL = '#F3EEFA';

const TIER_COLORS = {
  foundation: { bg: '#EFF6FF', text: '#1E40AF', border: '#BFDBFE' },
  operations:  { bg: '#F0FDF4', text: '#065F46', border: '#BBF7D0' },
  strategic:   { bg: '#FDF4FF', text: '#6B21A8', border: '#E9D5FF' },
};

const STATUS_COLORS = {
  active:    { bg: '#D1FAE5', text: '#065F46' },
  paused:    { bg: '#FEF3C7', text: '#92400E' },
  cancelled: { bg: '#FEE2E2', text: '#991B1B' },
};

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

export default function AiGovernance() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [programs, setPrograms]   = useState([]);
  const [tiers, setTiers]         = useState([]);
  const [clients, setClients]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating]   = useState(false);
  const [preview, setPreview]     = useState(null);
  const [filterStatus, setFilterStatus] = useState('active');

  const [form, setForm] = useState({
    client_id: '', tier_id: '', start_date: new Date().toISOString().split('T')[0],
    next_assessment_type: 'full', notes: '',
  });

  const load = async () => {
    setLoading(true);
    try {
      const [progsRes, tiersRes, clientsRes] = await Promise.all([
        aiGovApi.listPrograms(),
        aiGovApi.getTiers(),
        clientsApi.getAll(),
      ]);
      setPrograms(progsRes.data.programs || []);
      setTiers(tiersRes.data.tiers || []);
      setClients(clientsRes.data?.clients || []);
    } catch {
      toast.error('Failed to load AI Governance programs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const enrolledClientIds = new Set(programs.map(p => p.client_id));
  const availableClients  = clients.filter(c => !enrolledClientIds.has(c.id));

  const handleTierChange = async (tier_id) => {
    setForm(f => ({ ...f, tier_id }));
    if (!tier_id) { setPreview(null); return; }
    try {
      const res = await aiGovApi.billingPreview(parseInt(tier_id), 'monthly');
      setPreview(res.data);
    } catch {
      setPreview(null);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.client_id || !form.tier_id) {
      toast.error('Client and tier are required');
      return;
    }
    setCreating(true);
    try {
      await aiGovApi.createProgram({
        ...form,
        client_id: parseInt(form.client_id),
        tier_id:   parseInt(form.tier_id),
      });
      toast.success('AI Governance program created');
      setShowCreate(false);
      setForm({ client_id: '', tier_id: '', start_date: new Date().toISOString().split('T')[0], next_assessment_type: 'full', notes: '' });
      setPreview(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create program');
    } finally {
      setCreating(false);
    }
  };

  const filtered = filterStatus
    ? programs.filter(p => p.status === filterStatus)
    : programs;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0, color: PD, fontSize: 28, fontWeight: 700 }}>AI Governance</h2>
          <p style={{ margin: '4px 0 0', color: '#7E4DB3', fontSize: 14 }}>
            Manage client AI governance programs, assessments, and documentation.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {user?.role === 'admin' && (
            <button
              onClick={() => navigate('/ai-gov/admin')}
              style={outlineBtn}
            >
              Admin
            </button>
          )}
          <button onClick={() => setShowCreate(true)} style={primaryBtn}>
            + Enroll Client
          </button>
        </div>
      </div>

      {/* Stats bar */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
        {[
          { label: 'Total Programs', value: programs.length },
          { label: 'Active',    value: programs.filter(p => p.status === 'active').length },
          { label: 'Overdue Tasks', value: programs.reduce((s, p) => s + (p.overdue_tasks || 0), 0) },
        ].map(s => (
          <div key={s.label} style={{ background: 'white', border: `1px solid #E5D5F5`, borderRadius: 8, padding: '12px 20px', flex: 1 }}>
            <div style={{ fontSize: 24, fontWeight: 700, color: PD }}>{s.value}</div>
            <div style={{ fontSize: 13, color: '#7E4DB3' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filter */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {['active', 'paused', 'cancelled', ''].map(s => (
          <button
            key={s || 'all'}
            onClick={() => setFilterStatus(s)}
            style={{
              padding: '6px 14px', borderRadius: 20, fontSize: 13, cursor: 'pointer',
              border: `1px solid ${filterStatus === s ? P : '#E5D5F5'}`,
              background: filterStatus === s ? P : 'white',
              color: filterStatus === s ? 'white' : '#7E4DB3',
              fontWeight: filterStatus === s ? 600 : 400,
            }}
          >
            {s ? s.charAt(0).toUpperCase() + s.slice(1) : 'All'}
          </button>
        ))}
      </div>

      {/* Programs table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#7E4DB3' }}>Loading…</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#7E4DB3', background: 'white', borderRadius: 8, border: '1px solid #E5D5F5' }}>
          No programs found.{' '}
          {filterStatus === 'active' && (
            <button onClick={() => setShowCreate(true)} style={{ color: P, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
              Enroll your first client.
            </button>
          )}
        </div>
      ) : (
        <div style={{ background: 'white', borderRadius: 8, border: '1px solid #E5D5F5', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: PL }}>
                {['Client', 'Tier', 'Status', 'Start Date', 'Documents', 'Overdue Tasks', ''].map(h => (
                  <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: PD, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((p, i) => {
                const tc = TIER_COLORS[p.tier_code] || TIER_COLORS.foundation;
                const sc = STATUS_COLORS[p.status] || STATUS_COLORS.active;
                return (
                  <tr key={p.id}
                    style={{ borderTop: '1px solid #F3EEFA', background: i % 2 === 0 ? 'white' : '#FAFAFA', cursor: 'pointer' }}
                    onClick={() => navigate(`/ai-gov/programs/${p.id}`)}
                  >
                    <td style={{ padding: '12px 16px', fontWeight: 600, color: PD }}>{p.client_name}</td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ background: tc.bg, color: tc.text, border: `1px solid ${tc.border}`, borderRadius: 12, padding: '2px 10px', fontSize: 12, fontWeight: 600 }}>
                        {p.tier_name}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ background: sc.bg, color: sc.text, borderRadius: 12, padding: '2px 10px', fontSize: 12, fontWeight: 600 }}>
                        {p.status}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', color: '#374151', fontSize: 14 }}>{fmtDate(p.start_date)}</td>
                    <td style={{ padding: '12px 16px', color: '#374151', fontSize: 14 }}>{p.document_count || 0}</td>
                    <td style={{ padding: '12px 16px' }}>
                      {p.overdue_tasks > 0 ? (
                        <span style={{ background: '#FEE2E2', color: '#991B1B', borderRadius: 12, padding: '2px 10px', fontSize: 12, fontWeight: 600 }}>
                          {p.overdue_tasks} overdue
                        </span>
                      ) : (
                        <span style={{ color: '#9CA3AF', fontSize: 13 }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <button
                        onClick={e => { e.stopPropagation(); navigate(`/ai-gov/programs/${p.id}`); }}
                        style={{ ...outlineBtn, padding: '4px 12px', fontSize: 12 }}
                      >
                        Manage →
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Enroll modal */}
      {showCreate && (
        <div style={modalOverlay}>
          <div style={{ ...modalBox, width: 540 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0, color: PD, fontSize: 20, fontWeight: 700 }}>Enroll Client in AI Governance</h3>
              <button onClick={() => { setShowCreate(false); setPreview(null); }} style={closeBtn}>✕</button>
            </div>

            <form onSubmit={handleCreate}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <label style={labelStyle}>Client *</label>
                  <select
                    value={form.client_id}
                    onChange={e => setForm(f => ({ ...f, client_id: e.target.value }))}
                    style={inputStyle}
                    required
                  >
                    <option value="">Select a client…</option>
                    {availableClients.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                  {availableClients.length === 0 && (
                    <p style={{ margin: '4px 0 0', fontSize: 12, color: '#6B7280' }}>All clients are already enrolled.</p>
                  )}
                </div>

                <div>
                  <label style={labelStyle}>Tier *</label>
                  <select
                    value={form.tier_id}
                    onChange={e => handleTierChange(e.target.value)}
                    style={inputStyle}
                    required
                  >
                    <option value="">Select a tier…</option>
                    {tiers.map(t => (
                      <option key={t.id} value={t.id}>{t.name} — ${t.monthly_price}/mo</option>
                    ))}
                  </select>
                </div>

                {preview && (
                  <div style={{ background: PL, border: `1px solid #E5D5F5`, borderRadius: 8, padding: 14 }}>
                    <div style={{ fontWeight: 700, color: PD, marginBottom: 6 }}>{preview.tier.name} — Billing Preview</div>
                    <div style={{ color: '#374151', fontSize: 14 }}>
                      Monthly: <strong>${preview.tier.monthly_price}/mo</strong>
                      {' · '}Annual: <strong>${preview.tier.annual_price}/yr</strong>
                      {' · '}Save <strong>${(preview.tier.monthly_price * 12 - preview.tier.annual_price).toFixed(2)}/yr</strong> with annual billing
                    </div>
                    {preview.tier.features && (
                      <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 13, color: '#4B5563' }}>
                        {(typeof preview.tier.features === 'string'
                          ? JSON.parse(preview.tier.features)
                          : preview.tier.features
                        ).map((f, i) => <li key={i}>{f}</li>)}
                      </ul>
                    )}
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                  <div>
                    <label style={labelStyle}>Start Date *</label>
                    <input type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} style={inputStyle} required />
                  </div>
                  <div>
                    <label style={labelStyle}>First Assessment</label>
                    <select value={form.next_assessment_type} onChange={e => setForm(f => ({ ...f, next_assessment_type: e.target.value }))} style={inputStyle}>
                      <option value="full">Full (72 questions)</option>
                      <option value="core">Core (27 questions)</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label style={labelStyle}>Notes</label>
                  <textarea
                    value={form.notes}
                    onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                    rows={2}
                    style={{ ...inputStyle, resize: 'vertical' }}
                    placeholder="Optional notes…"
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
                <button type="button" onClick={() => { setShowCreate(false); setPreview(null); }} style={outlineBtn} disabled={creating}>Cancel</button>
                <button type="submit" style={primaryBtn} disabled={creating}>
                  {creating ? 'Creating…' : 'Enroll Client'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Shared styles ──────────────────────────────────────────────────────────────
const primaryBtn = {
  padding: '8px 18px', backgroundColor: '#6B3FA0', color: 'white',
  border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14, fontWeight: 600,
};
const outlineBtn = {
  padding: '8px 18px', backgroundColor: 'transparent', color: '#6B3FA0',
  border: '2px solid #6B3FA0', borderRadius: 6, cursor: 'pointer', fontSize: 14, fontWeight: 600,
};
const modalOverlay = {
  position: 'fixed', inset: 0, background: 'rgba(74,43,112,0.4)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
};
const modalBox = {
  background: 'white', borderRadius: 12, padding: 28,
  boxShadow: '0 20px 60px rgba(74,43,112,0.2)', maxHeight: '90vh', overflowY: 'auto',
};
const closeBtn = {
  background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#9CA3AF',
};
const labelStyle = { display: 'block', marginBottom: 4, fontSize: 13, fontWeight: 600, color: '#4A2B70' };
const inputStyle = {
  width: '100%', padding: '8px 10px', border: '1px solid #E5D5F5', borderRadius: 6,
  fontSize: 14, color: '#374151', boxSizing: 'border-box', outline: 'none',
};
